import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PayoutMethodOption, PayoutRequest } from '@mlm/shared';
import { apiErrorMessage, fmtPeso, payouts, wallet } from '../lib/api';
import { qk } from '../lib/queries';
import { instantDay } from '../lib/format';
import {
  Block,
  CoachButton,
  Eyebrow,
  ErrorNote,
  Loading,
  Screen,
  inputClass,
} from '../components/primitives';
import { ShopNav } from '../components/shop-nav';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COPY: Record<PayoutRequest['status'], string> = {
  requested: 'Requested',
  queued: 'Queued for payout',
  processing: 'Being processed',
  paid: 'Paid',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
};

/**
 * Cash out. Every rule here is also enforced server-side; the client reads the
 * same settings only to explain a refusal BEFORE the member fills in a form
 * that was never going to be accepted.
 *
 * The deduction breakdown is rendered from `preview.steps`, never a hardcoded
 * service/admin/tax order: the admin can reorder them globally, and a fixed
 * sequence would show a member a different sum from the one they are charged.
 */
export function CashOutPage() {
  const qc = useQueryClient();
  const [slotId, setSlotId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [methodCode, setMethodCode] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  const slots = useQuery({ queryKey: qk.mySlots, queryFn: wallet.slots, staleTime: 5 * 60_000 });
  const win = useQuery({ queryKey: qk.cashoutWindow, queryFn: payouts.window, staleTime: 60_000 });
  const methods = useQuery({
    queryKey: qk.payoutMethods,
    queryFn: payouts.methods,
    staleTime: 5 * 60_000,
  });
  const qrs = useQuery({ queryKey: qk.payoutQrs, queryFn: payouts.qrs, staleTime: 5 * 60_000 });
  const requests = useQuery({
    queryKey: qk.payoutRequests,
    queryFn: payouts.requests,
    staleTime: 30_000,
  });

  const active = slotId ?? slots.data?.[0]?.id ?? null;
  const method: PayoutMethodOption | null =
    methods.data?.find((m) => m.code === methodCode) ?? null;

  const balance = useQuery({
    queryKey: qk.walletBalance(active),
    queryFn: () => wallet.balance(active),
    enabled: Boolean(active),
    staleTime: 60_000,
  });

  // Debounced so typing an amount does not fire a preview per keystroke.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(amount), 400);
    return () => clearTimeout(t);
  }, [amount]);

  const preview = useQuery({
    queryKey: ['wallet', 'cashout', 'preview', active, debounced, methodCode],
    queryFn: () => payouts.preview({ slotId: active!, amount: debounced, method: methodCode! }),
    enabled: Boolean(active && methodCode && Number(debounced) > 0),
  });

  const pending = (requests.data ?? []).filter(
    (r) => r.status === 'requested' || r.status === 'queued' || r.status === 'processing',
  );

  // Why the member cannot submit, in the order the server would refuse.
  const blocked = useMemo<string | null>(() => {
    if (!slots.isPending && (slots.data?.length ?? 0) === 0)
      return 'Cash-out needs a membership slot. Activate one first.';
    if (win.data && !win.data.open) return 'The cash-out window is closed right now.';
    if (win.data?.requireKyc)
      return 'This account needs verified ID before cashing out. Finish that in the web portal.';
    if (win.data && !win.data.multipleCashoutRequestsAllowed && pending.length > 0)
      return 'You already have a request being processed. Wait for it to finish.';
    const needsQr = Boolean(win.data?.payoutQrRequired) || Boolean(method?.requiresQr);
    if (needsQr && methodCode && !(qrs.data?.items ?? []).some((q) => q.method === methodCode))
      return 'This method needs a saved payment QR. Add one in the web portal first.';
    return null;
  }, [slots.isPending, slots.data, win.data, pending.length, method, methodCode, qrs.data]);

  const submit = useMutation({
    mutationFn: () =>
      payouts.create({
        slotId: active!,
        amount,
        method: methodCode!,
        payload: fields,
      }),
    onSuccess: async () => {
      setAmount('');
      setFields({});
      await qc.invalidateQueries({ queryKey: qk.payoutRequests });
      await qc.invalidateQueries({ queryKey: qk.walletBalance(active) });
    },
    onError: setError,
  });

  const cancel = useMutation({
    mutationFn: (id: string) => payouts.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.payoutRequests }),
    onError: setError,
  });

  const missingRequired = (method?.fields ?? []).some(
    (f) => f.required && !(fields[f.key] ?? '').trim(),
  );
  const canSubmit =
    !blocked &&
    Boolean(active && methodCode) &&
    Number(amount) > 0 &&
    !missingRequired &&
    preview.data?.sufficient === true &&
    preview.data?.netPositive === true &&
    !submit.isPending;

  return (
    <Screen eyebrow="Wallet" title="Cash out" wide>
      <ShopNav />

      {slots.isPending || win.isPending || methods.isPending ? (
        <Loading label="Loading" />
      ) : win.error ? (
        <ErrorNote error={win.error} retry={() => void win.refetch()} />
      ) : (
        <>
          {win.data && !win.data.open && win.data.schedule.enabled && (
            <div className="border-b border-border px-5 py-5">
              <Eyebrow>Closed</Eyebrow>
              <p className="mt-2 text-[0.93rem] leading-relaxed text-ink/80">
                Cash-out opens{' '}
                {win.data.schedule.daysOfWeek.map((d) => DAYS[d] ?? String(d)).join(', ')} between{' '}
                {win.data.schedule.openHour}:00 and {win.data.schedule.closeHour}:00 (
                {win.data.timezone}).
              </p>
            </div>
          )}

          {blocked && (
            <div className="border-b border-border px-5 py-4">
              <p className="border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] leading-relaxed text-ink/85">
                {blocked}
              </p>
            </div>
          )}

          {(slots.data?.length ?? 0) > 0 && (
            <>
              <Block eyebrow="From" title="Which slot">
                {(slots.data?.length ?? 0) > 1 ? (
                  <select
                    aria-label="Slot"
                    value={active ?? ''}
                    onChange={(e) => setSlotId(e.target.value)}
                    className="mt-3 min-h-11 w-full border border-[rgba(16,32,26,0.22)] bg-background px-3 text-[0.93rem] text-ink"
                  >
                    {slots.data!.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.slotCode}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1">{slots.data?.[0]?.slotCode}</p>
                )}
                <p className="mt-3 text-[0.93rem]">
                  Available: <strong>{fmtPeso(balance.data?.available ?? '0')}</strong>
                </p>
              </Block>

              <Block eyebrow="Amount" title="How much">
                <label className="sr-only" htmlFor="cashout-amount">
                  Amount
                </label>
                <input
                  id="cashout-amount"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  placeholder="0.00"
                  className={`${inputClass} mt-3`}
                />
                {win.data?.limits.perSlotMin != null && (
                  <p className="mt-2 text-[0.85rem] text-sage-deep">
                    Minimum {fmtPeso(String(win.data.limits.perSlotMin))}
                    {win.data.limits.perSlotMax != null
                      ? `, maximum ${fmtPeso(String(win.data.limits.perSlotMax))}`
                      : ''}
                    .
                  </p>
                )}
              </Block>

              <Block eyebrow="To" title="Where should it go">
                <ul className="mt-3 space-y-2">
                  {(methods.data ?? []).map((m) => (
                    <li key={m.code}>
                      <button
                        type="button"
                        onClick={() => {
                          setMethodCode(m.code);
                          setFields({});
                        }}
                        className={[
                          'flex min-h-11 w-full items-center justify-between border px-4 py-3 text-left transition-colors',
                          m.code === methodCode
                            ? 'border-verde bg-surface'
                            : 'border-[rgba(16,32,26,0.22)] hover:border-[rgba(16,32,26,0.5)]',
                        ].join(' ')}
                      >
                        <span className="text-[0.93rem] text-ink">{m.label}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                {method && (
                  <div className="mt-4 space-y-3">
                    {(method.fields.length > 0
                      ? method.fields
                      : [{ key: 'account', label: method.accountLabel, required: true }]
                    ).map((f) => (
                      <div key={f.key}>
                        <label className="eyebrow" htmlFor={`f-${f.key}`}>
                          {f.label}
                          {f.required ? '' : ' (optional)'}
                        </label>
                        {'options' in f && f.options && f.options.length > 0 ? (
                          <select
                            id={`f-${f.key}`}
                            value={fields[f.key] ?? ''}
                            onChange={(e) => setFields((s) => ({ ...s, [f.key]: e.target.value }))}
                            className="mt-1 min-h-11 w-full border border-[rgba(16,32,26,0.22)] bg-background px-3 text-[0.93rem] text-ink"
                          >
                            <option value="">Choose</option>
                            {f.options.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`f-${f.key}`}
                            value={fields[f.key] ?? ''}
                            onChange={(e) => setFields((s) => ({ ...s, [f.key]: e.target.value }))}
                            placeholder={'placeholder' in f ? (f.placeholder ?? '') : ''}
                            className={`${inputClass} mt-1`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Block>

              {preview.data && (
                <Block eyebrow="Breakdown" title="What you receive">
                  <dl className="mt-3 space-y-2 text-[0.93rem]">
                    <Line label="Requested" value={fmtPeso(preview.data.gross)} />
                    {/* Order comes from the server: the admin can reorder these. */}
                    {(preview.data.steps ?? []).map((s) => (
                      <Line
                        key={s.key}
                        label={s.mode === 'percent' ? `${s.label} (${Number(s.value)}%)` : s.label}
                        value={`-${fmtPeso(s.amount)}`}
                      />
                    ))}
                    <Line label="You receive" value={fmtPeso(preview.data.net)} strong />
                  </dl>
                  {!preview.data.sufficient && (
                    <p className="mt-3 text-[0.9rem] text-ink/80">
                      That is more than your available balance.
                    </p>
                  )}
                  {preview.data.sufficient && !preview.data.netPositive && (
                    <p className="mt-3 text-[0.9rem] text-ink/80">
                      After deductions this would pay out nothing. Request a larger amount.
                    </p>
                  )}
                </Block>
              )}

              <div className="px-5 py-6">
                {error != null && (
                  <p className="mb-4 border border-[rgba(176,141,63,0.4)] px-4 py-3 text-[0.9rem] text-ink/85">
                    {apiErrorMessage(error)}
                  </p>
                )}
                <CoachButton
                  kind="gold"
                  block
                  disabled={!canSubmit}
                  onClick={() => {
                    setError(null);
                    submit.mutate();
                  }}
                >
                  {submit.isPending ? 'Sending your request' : 'Request cash out'}
                </CoachButton>
              </div>
            </>
          )}

          <p className="border-b border-t border-border px-5 py-3 font-mono text-[0.62rem] uppercase tracking-[0.14em] text-sage-deep">
            Your requests
          </p>
          {requests.isPending ? (
            <Loading label="Loading requests" />
          ) : (requests.data?.length ?? 0) === 0 ? (
            <p className="px-5 py-10 text-center text-[0.93rem] text-ink/70">Nothing yet.</p>
          ) : (
            <ul>
              {requests.data!.map((r) => (
                <li
                  key={r.id}
                  className="flex items-start justify-between gap-4 border-b border-border px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-[0.93rem] text-ink">{fmtPeso(r.netAmount)}</p>
                    <p className="mt-1 text-[0.85rem] text-sage-deep">
                      {STATUS_COPY[r.status]} · {instantDay(r.requestedAt)}
                    </p>
                  </div>
                  {win.data?.cancellationAllowed && r.status === 'requested' && (
                    <CoachButton kind="quiet" onClick={() => cancel.mutate(r.id)}>
                      Cancel
                    </CoachButton>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Screen>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className={strong ? 'text-ink' : 'text-ink/70'}>{label}</dt>
      <dd className={strong ? 'font-display text-[1.15rem] font-semibold text-ink' : 'text-ink'}>
        {value}
      </dd>
    </div>
  );
}
